import React, { useMemo, useState } from "react";
import { Button, Input, Offcanvas, OffcanvasBody, OffcanvasHeader, Spinner } from "reactstrap";
import TableContainer from "../../../../Components/Common/TableContainer";

 type StockTransferenciasDetailOffcanvasProps = {
   isOpen: boolean;
   onClose: () => void;
   title: string;
   description: string;
   columns: any[];
   data: any[];
   searchPlaceholder?: string;
   onExportExcel: () => void;
   onExportPdf: () => void;
   disableActions?: boolean;
   exportingExcel?: boolean;
   exportingPdf?: boolean;
   totals: {
     totalCantidad: number;
     promedioCostoSinIva: number | null;
     promedioCostoConIva: number | null;
     totalValorSinIva: number;
     totalValorConIva: number;
   };
 };
 
const StockTransferenciasDetailOffcanvas: React.FC<StockTransferenciasDetailOffcanvasProps> = ({
  isOpen,
  onClose,
  title,
  description,
   columns,
   data,
   searchPlaceholder = "Buscar en movimientos…",
   onExportExcel,
   onExportPdf,
   disableActions = false,
   exportingExcel = false,
  exportingPdf = false,
  totals,
}) => {
  const [searchValue, setSearchValue] = useState("");

  const filteredData = useMemo(() => {
    if (!searchValue.trim()) {
      return data;
    }
    const needle = searchValue.toLowerCase();
    return data.filter((row) => {
      if (!row || typeof row !== "object") {
        return false;
      }
      return Object.values(row).some((value) => {
        if (value == null) return false;
        if (typeof value === "string") {
          return value.toLowerCase().includes(needle);
        }
        if (typeof value === "number") {
          return value.toString().includes(needle);
        }
        if (Array.isArray(value)) {
          return value.some((item) => String(item ?? "").toLowerCase().includes(needle));
        }
        return String(value).toLowerCase().includes(needle);
      });
    });
  }, [data, searchValue]);

  return (
  <Offcanvas
    isOpen={isOpen}
    toggle={onClose}
     direction="end"
     className="offcanvas-end border-0"
     style={{ width: "100%", maxWidth: "100%" }}
   >
     <OffcanvasHeader toggle={onClose} className="border-bottom align-items-start gap-3">
       <div>
         <div className="fs-5 fw-semibold text-body">{title}</div>
         <small className="text-muted">{description}</small>
       </div>
       <div className="d-flex gap-2 ms-auto">
         <Button color="success" size="sm" onClick={onExportExcel} disabled={disableActions || exportingExcel}>
           {exportingExcel ? (
             <>
               <Spinner size="sm" className="me-1" /> Exportando
             </>
           ) : (
             <>
               <i className="ri-download-2-line me-1" /> Excel
             </>
           )}
         </Button>
         <Button color="danger" size="sm" onClick={onExportPdf} disabled={disableActions || exportingPdf}>
           {exportingPdf ? (
             <>
               <Spinner size="sm" className="me-1" /> Generando
             </>
           ) : (
             <>
               <i className="ri-printer-line me-1" /> PDF
             </>
           )}
         </Button>
      </div>
    </OffcanvasHeader>
    <OffcanvasBody className="d-flex flex-column p-0">
       <div className="border-bottom bg-body-secondary-subtle px-3 py-2 d-flex flex-wrap gap-4 small text-uppercase text-muted fw-semibold">
         <div>
           <div>Cantidad</div>
           <div className="text-body fw-bold fs-6 text-uppercase">
             {totals.totalCantidad.toLocaleString("es-PY", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
           </div>
         </div>
         <div>
           <div>Costo unitario (s/IVA)</div>
           <div className="text-body fw-bold">
             {totals.promedioCostoSinIva == null
               ? "—"
               : `Gs. ${totals.promedioCostoSinIva.toLocaleString("es-PY", {
                   minimumFractionDigits: 0,
                   maximumFractionDigits: 2,
                 })}`}
           </div>
         </div>
         <div>
           <div>Costo unitario (c/IVA)</div>
           <div className="text-body fw-bold">
             {totals.promedioCostoConIva == null
               ? "—"
               : `Gs. ${totals.promedioCostoConIva.toLocaleString("es-PY", {
                   minimumFractionDigits: 0,
                   maximumFractionDigits: 2,
                 })}`}
           </div>
         </div>
         <div>
           <div>Valor s/IVA</div>
           <div className="text-body fw-bold">
             {`Gs. ${totals.totalValorSinIva.toLocaleString("es-PY", {
               minimumFractionDigits: 0,
               maximumFractionDigits: 2,
             })}`}
           </div>
         </div>
         <div>
           <div>Valor c/IVA</div>
           <div className="text-body fw-bold">
             {`Gs. ${totals.totalValorConIva.toLocaleString("es-PY", {
               minimumFractionDigits: 0,
               maximumFractionDigits: 2,
             })}`}
           </div>
         </div>
       </div>
       <div className="flex-grow-1 overflow-auto p-3">
        <div className="mb-3 mt-3">
          <Input
            type="search"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </div>
        {filteredData.length > 0 ? (
          <TableContainer
            columns={columns}
            data={filteredData}
            isGlobalFilter={false}
            divClass="table-responsive mb-1 inventory-table-scroll"
            tableClass="mb-0 align-middle table-borderless"
            theadClass="table-light text-muted"
            SearchPlaceholder={searchPlaceholder}
            customPageSize={25}
          />
        ) : (
          <div className="py-5 text-center text-muted">No encontramos movimientos para esta agrupación.</div>
        )}
       </div>
    </OffcanvasBody>
  </Offcanvas>
  );
};
 
 export default StockTransferenciasDetailOffcanvas;
